import type { Role } from './roles';

export interface DepartmentDefinition {
  code: string;
  name: string;
  /** Roles whose users belong to this department by default. */
  roles: Role[];
  /** Names used for this department in the legacy Sheet (Makin_Users.department and WA department strings). */
  legacyNames: string[];
}

export const BUILTIN_DEPARTMENTS: DepartmentDefinition[] = [
  { code: 'MGMT', name: 'Management', roles: ['super_admin', 'admin'], legacyNames: ['Super Admin', 'Admin', 'Management'] },
  { code: 'BD', name: 'Business Development', roles: ['bd'], legacyNames: ['BD', 'Business Development'] },
  { code: 'RA', name: 'Regulatory Affairs', roles: ['ra'], legacyNames: ['RA', 'Regulatory Affairs'] },
  { code: 'MIS', name: 'MIS', roles: ['mis'], legacyNames: ['MIS'] },
  { code: 'QA', name: 'Quality Assurance', roles: ['qa'], legacyNames: ['QA'] },
  { code: 'QC', name: 'Quality Control', roles: ['qc'], legacyNames: ['QC'] },
  { code: 'FND', name: 'Formulation & Development', roles: ['fnd'], legacyNames: ['FND', 'F&D'] },
  {
    code: 'PROD_OSD',
    name: 'Production – OSD',
    roles: ['production_osd'],
    legacyNames: ['Production OSD', 'Tablet', 'Capsules', 'Powder', 'Grenules', 'Granules'],
  },
  { code: 'PROD_LIQUID', name: 'Production – Liquid', roles: ['production_liquid'], legacyNames: ['Production Liquid', 'Liquid'] },
  { code: 'PROD_SG', name: 'Production – Soft Gel', roles: ['production_sg'], legacyNames: ['Production SG', 'Soft Gel'] },
  { code: 'PPIC', name: 'PPIC', roles: ['ppic'], legacyNames: ['PPIC', 'PPIC Department'] },
  { code: 'RM_STORE', name: 'RM Store', roles: ['rm_store'], legacyNames: ['RM Store'] },
  { code: 'PM_STORE', name: 'PM Store', roles: ['pm_store'], legacyNames: ['PM Store'] },
  { code: 'DESIGN', name: 'Design', roles: ['design'], legacyNames: ['Design', 'Design Department'] },
  { code: 'ARTWORK', name: 'Artwork', roles: ['artwork'], legacyNames: ['Artwork', 'Artwork Checking', 'Artwork Department'] },
  { code: 'PURCHASE', name: 'Purchase', roles: [], legacyNames: ['Purchase'] },
  { code: 'PACKING', name: 'Packing', roles: [], legacyNames: ['Packing Department'] },
  { code: 'PACKING_DEV', name: 'Packing Development', roles: [], legacyNames: ['Packing Development'] },
  { code: 'COSMETICS', name: 'Cosmetics', roles: [], legacyNames: ['Cosmetics'] },
  { code: 'BSR', name: 'BSR', roles: [], legacyNames: ['BSR'] },
  { code: 'ACCOUNTS', name: 'Accounts', roles: [], legacyNames: ['Account', 'Accounts'] },
  { code: 'IT', name: 'IT', roles: [], legacyNames: ['IT'] },
];

const byLegacyName = new Map<string, string>();
for (const dept of BUILTIN_DEPARTMENTS) {
  for (const name of [dept.name, ...dept.legacyNames]) byLegacyName.set(name.trim().toLowerCase(), dept.code);
}

/** Maps a legacy department label (case-insensitive) to a built-in department code. */
export function departmentCodeForLegacyName(name: unknown): string | null {
  if (name === null || name === undefined) return null;
  return byLegacyName.get(String(name).trim().toLowerCase()) ?? null;
}

/** Default department for a role, e.g. `qa` → `QA`. */
export function defaultDepartmentForRole(role: Role): string | null {
  return BUILTIN_DEPARTMENTS.find((d) => d.roles.includes(role))?.code ?? null;
}

/** Department codes are upper snake case; custom departments get one derived from their name. */
export function departmentCodeFromName(name: string): string {
  const code = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/&/g, ' AND ')
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return code || 'DEPT';
}
